import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdFlag, MdPerson, MdExpandMore, MdExpandLess, MdAccountBalance, MdLocationOn } from 'react-icons/md';
import VerifiedBadge from '../components/VerifiedBadge';

function AdminFlaggedReps() {
  const [flaggedReps, setFlaggedReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRep, setExpandedRep] = useState(null);
  const [repDetails, setRepDetails] = useState({});

  useEffect(() => { loadFlaggedReps(); }, []);

  const loadFlaggedReps = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/flagged-representatives`);
      setFlaggedReps(res.data.flaggedReps || []);
    } catch (err) {
      console.error('Failed to load flagged representatives:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDetails = async (profileId) => {
    if (repDetails[profileId]) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/flagged-representatives/${profileId}`);
      setRepDetails(prev => ({ ...prev, [profileId]: res.data }));
    } catch (err) {
      console.error('Failed to load flag details:', err);
    }
  };

  const handleExpand = async (profileId) => {
    if (expandedRep === profileId) {
      setExpandedRep(null);
    } else {
      setExpandedRep(profileId);
      await loadDetails(profileId);
    }
  };

  const handleDismiss = async (profileId) => {
    if (!window.confirm('Dismiss all flags for this representative?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/flagged-representatives/${profileId}/dismiss`);
      loadFlaggedReps();
      setExpandedRep(null);
    } catch (err) {
      alert('Failed to dismiss flags');
    }
  };

  const handleSuspend = async (rep) => {
    if (!window.confirm(`Suspend ${rep.name}'s profile? This will deactivate their profile, cancel their subscription, and notify them.`)) return;
    try {
      await axios.post(`${API_URL}/api/admin/flagged-representatives/${rep.profileId}/suspend`);
      loadFlaggedReps();
      setExpandedRep(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to suspend profile');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-flagged-reps-page">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800 font-rubik flex items-center gap-2">
          <MdFlag className="text-red-500" />
          Flagged Representatives
        </h2>
        <span className="text-sm text-gray-600 font-rubik">
          {flaggedReps.length} profile{flaggedReps.length !== 1 ? 's' : ''} flagged
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : flaggedReps.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-12">
          <MdFlag className="text-6xl text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-rubik">No flagged representative profiles</p>
          <p className="text-gray-400 text-sm font-rubik mt-2">All clear! No profiles have been reported.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {flaggedReps.map(rep => (
            <div key={rep.profileId} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid={`flagged-rep-${rep.profileId}`}>
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {rep.profilePictureUrl ? (
                      <img src={rep.profilePictureUrl} alt={rep.name} className="w-full h-full object-cover" />
                    ) : (
                      <MdPerson className="text-2xl text-gray-400" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <Link to={`/representatives/${rep.profileId}`} className="font-semibold text-gray-900 hover:text-primary flex items-center gap-1 font-rubik">
                      {rep.name}
                      <VerifiedBadge status={rep.verificationStatus} />
                    </Link>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-0.5">
                      {rep.office && (
                        <span className="flex items-center gap-1">
                          <MdAccountBalance className="text-sm" /> {rep.office}
                          {rep.officeLevel && <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{rep.officeLevel}</span>}
                        </span>
                      )}
                      {rep.location && (
                        <span className="flex items-center gap-1">
                          <MdLocationOn className="text-sm" /> {rep.location}
                        </span>
                      )}
                    </div>
                    <span className="text-red-500 font-medium text-sm flex items-center gap-1 mt-1 font-rubik">
                      <MdFlag className="text-sm" />
                      {rep.flagCount} report{rep.flagCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleExpand(rep.profileId)}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-1 transition-colors"
                    data-testid={`expand-rep-flags-${rep.profileId}`}
                  >
                    {expandedRep === rep.profileId ? <><MdExpandLess /> Hide</> : <><MdExpandMore /> Details</>}
                  </button>
                  <button
                    onClick={() => handleDismiss(rep.profileId)}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    data-testid={`dismiss-rep-flags-${rep.profileId}`}
                  >
                    Dismiss
                  </button>
                  <button
                    onClick={() => handleSuspend(rep)}
                    className="px-3 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors"
                    data-testid={`suspend-rep-${rep.profileId}`}
                  >
                    Suspend
                  </button>
                </div>
              </div>

              {expandedRep === rep.profileId && repDetails[rep.profileId] && (
                <div className="mt-5 pt-5 border-t border-gray-200">
                  <h4 className="text-base font-semibold text-gray-800 mb-3 font-rubik">
                    Reports ({repDetails[rep.profileId].totalFlags})
                  </h4>
                  <div className="space-y-3">
                    {repDetails[rep.profileId].flags.map(flag => (
                      <div key={flag.flagId} className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid={`rep-flag-detail-${flag.flagId}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <MdPerson className="text-gray-500" />
                              <span className="font-medium text-gray-800 font-rubik">{flag.username}</span>
                            </div>
                            <p className="text-sm text-gray-700 font-rubik">
                              <span className="font-medium">Reason:</span> {flag.reason}
                            </p>
                          </div>
                          <span className="text-xs text-gray-500 font-rubik">
                            {new Date(flag.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminFlaggedReps;
